import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { useSession } from '@/providers/session-provider';
import {
    useAndieChatMessages,
    useAndieChatControls,
    type HarnessPlanData,
} from '@/providers/andie-chat-provider';
import type { UIMessage } from 'ai';
import {
    AndieChatMessagesDocument,
    AndieChatThreadsDocument,
} from '@/graphql/generated/andie-history.generated';

// Backend-generated thread titles (first message + plan) overlap the run for multi-step runs but can
// trail a fast single-step run by a beat; refetch twice so the new title reliably appears in history.
const HISTORY_REFRESH_DELAYS_MS = [1500, 3500];

const useRefreshAndieHistoryOnLoadingComplete = (
    apolloClient: ReturnType<typeof useApolloClient>,
    isLoading: boolean,
) => {
    const wasLoadingRef = useRef(false);
    const historyRefreshTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    useEffect(() => {
        if (isLoading) {
            wasLoadingRef.current = true;
            return;
        }
        if (!wasLoadingRef.current) return;
        wasLoadingRef.current = false;

        const refetchHistory = () => {
            apolloClient.refetchQueries({ include: [AndieChatThreadsDocument] }).catch(() => {});
        };

        historyRefreshTimeoutsRef.current.forEach(clearTimeout);
        historyRefreshTimeoutsRef.current = HISTORY_REFRESH_DELAYS_MS.map((delay) =>
            setTimeout(refetchHistory, delay),
        );
    }, [apolloClient, isLoading]);

    useEffect(
        () => () => {
            historyRefreshTimeoutsRef.current.forEach(clearTimeout);
        },
        [],
    );
};

export const AndieChatManagement = () => {
    const { messages, chatId } = useAndieChatMessages();
    const { setMessages, setThreadId, hydrateHarnessPlan, isLoading, isHydrated, setHydrated } =
        useAndieChatControls();
    const { workspace } = useSession();
    const apolloClient = useApolloClient();
    useRefreshAndieHistoryOnLoadingComplete(apolloClient, isLoading);
    const threadStorageKey = workspace ? `andie-thread-v1-${workspace.id}` : 'andie-thread-v1';

    const messagesRef = useRef(messages);
    messagesRef.current = messages;
    const chatIdRef = useRef(chatId);
    chatIdRef.current = chatId;

    useEffect(() => {
        let cancelled = false;
        setHydrated(false);

        const setNewThread = () => {
            const nextThreadId = crypto.randomUUID();
            localStorage.setItem(threadStorageKey, nextThreadId);
            setThreadId(nextThreadId);
            setMessages([]);
            setHydrated(true);
        };

        const hydrateThread = async () => {
            try {
                const storedThreadId = localStorage.getItem(threadStorageKey);
                const { data: threadsData } = await apolloClient.query({
                    query: AndieChatThreadsDocument,
                    fetchPolicy: 'network-only',
                });

                if (cancelled) return;

                const threads = threadsData?.andieChatThreads.threads ?? [];
                const selectedThread = storedThreadId
                    ? threads.find((thread) => thread.id === storedThreadId)
                    : null;
                if (!selectedThread) {
                    setNewThread();
                    return;
                }

                const { data: messagesData } = await apolloClient.query({
                    query: AndieChatMessagesDocument,
                    variables: { threadId: selectedThread.id },
                    fetchPolicy: 'network-only',
                });

                if (cancelled) return;

                localStorage.setItem(threadStorageKey, selectedThread.id);
                setThreadId(selectedThread.id);
                const threadMessages = messagesData?.andieChatMessages.messages;
                setMessages(Array.isArray(threadMessages) ? (threadMessages as UIMessage[]) : []);
                hydrateHarnessPlan(
                    (messagesData?.andieChatMessages.plan as HarnessPlanData | null) ?? null,
                );
                setHydrated(true);
            } catch {
                if (cancelled) return;
                setNewThread();
            }
        };

        hydrateThread();

        return () => {
            cancelled = true;
        };
    }, [
        apolloClient,
        setHydrated,
        setMessages,
        setThreadId,
        hydrateHarnessPlan,
        threadStorageKey,
        workspace?.id,
    ]);

    useEffect(() => {
        if (!isHydrated || !chatId) return;
        localStorage.setItem(threadStorageKey, chatId);
    }, [chatId, isHydrated, threadStorageKey]);

    const wasLoadingRef = useRef(false);
    useEffect(() => {
        if (isLoading) {
            wasLoadingRef.current = true;
            return;
        }
        if (!wasLoadingRef.current) return;
        wasLoadingRef.current = false;

        const msgs = messagesRef.current;
        if (msgs.length === 0) return;

        localStorage.setItem(threadStorageKey, chatIdRef.current);
    }, [isLoading, threadStorageKey]);
    return null;
};
